<template>
  <div
      class="ei-scale-table"
      :style="cssVars"
      :class="{
      'ei-scale-table--compare': mode === 'compare',
    }"
  >
    <template
        v-for="(section, sectionIndex) in normalizedSections"
        :key="section.key || sectionIndex"
    >
      <div
          v-if="shouldShowSectionTitle(section, sectionIndex)"
          class="ei-scale-table__section-title"
          :class="section.key ?? ''"
      >
        {{ section.title }}
      </div>

      <div class="ei-scale-table__rows">
        <div
            class="ei-scale-table__rows-guides"
            aria-hidden="true"
        >
          <span
              v-for="position in guidePositions"
              :key="position"
              class="ei-scale-table__rows-guide"
              :style="{ left: position + '%' }"
          ></span>
        </div>

        <div
            v-for="row in section.rows"
            :key="row.key || row.label"
            class="ei-scale-table__row"
            :class="{
            'ei-scale-table__row_muted': row.muted,
          }"
        >
          <div class="ei-scale-table__row-header">
            <div class="ei-scale-table__label">
              <span>{{ row.label }}</span>

              <a-popover
                  v-if="row.helpText"
                  trigger="hover"
                  placement="top"
              >
                <template #content>
                  <div class="ei-scale-table__popover">
                    {{ row.helpText }}
                  </div>
                </template>

                <QuestionCircleOutlined class="ei-scale-table__help-icon" />
              </a-popover>
            </div>

            <div class="ei-scale-table__right">
              <span
                  v-if="mode === 'compare' && showDifference"
                  class="ei-scale-table__difference"
                  :class="getDifferenceClass(row.difference)"
              >
                {{ formatDifference(row.difference) }}
              </span>

              <span class="ei-scale-table__value">
                {{ formatValue(row.value) }}
              </span>
            </div>
          </div>

          <div
              v-if="mode === 'compare'"
              class="ei-scale-table__compare-area"
          >
            <div class="ei-scale-table__track">
              <div
                  class="ei-scale-table__fill"
                  :style="{
                  width: getWidth(row.compareValue) + '%',
                  backgroundColor: row.compareColor,
                }"
              ></div>

              <span
                  v-if="showTrackValues"
                  class="ei-scale-table__track-value ei-scale-table__track-value--compare"
                  :style="{ left: getWidth(row.compareValue) + '%' }"
              >
                {{ formatValue(row.compareValue) }}
              </span>
            </div>

            <div class="ei-scale-table__track">
              <div
                  class="ei-scale-table__fill"
                  :style="{
                  width: getWidth(row.value) + '%',
                  backgroundColor: row.color,
                }"
              ></div>

              <span
                  v-if="showTrackValues"
                  class="ei-scale-table__track-value ei-scale-table__track-value--main"
                  :style="{ left: getWidth(row.value) + '%' }"
              >
                {{ formatValue(row.value) }}
              </span>
            </div>
          </div>

          <div
              v-else
              class="ei-scale-table__scale-area"
          >
            <div class="ei-scale-table__track">
              <div
                  class="ei-scale-table__fill"
                  :style="{
                  width: getWidth(row.value) + '%',
                  backgroundColor: row.color,
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div
          v-if="showScaleAfterEachSection"
          class="ei-scale-table__scale"
      >
        <span
            v-for="item in scaleLabels"
            :key="item.key"
            class="ei-scale-table__scale-item"
            :class="{
            'ei-scale-table__scale-item--number': item.type === 'number',
            'ei-scale-table__scale-item--label': item.type === 'label',
            'ei-scale-table__scale-item--first': item.key === 'min',
            'ei-scale-table__scale-item--last': item.key === 'max',
          }"
            :style="{ left: item.position + '%' }"
            :title="item.text"
        >
          {{ item.text }}
        </span>
      </div>
    </template>

    <div
        v-if="!showScaleAfterEachSection"
        class="ei-scale-table__scale ei-scale-table__bottom-scale"
    >
      <span
          v-for="item in scaleLabels"
          :key="item.key"
          class="ei-scale-table__scale-item"
          :class="{
          'ei-scale-table__scale-item--number': item.type === 'number',
          'ei-scale-table__scale-item--label': item.type === 'label',
          'ei-scale-table__scale-item--first': item.key === 'min',
          'ei-scale-table__scale-item--last': item.key === 'max',
        }"
          :style="{ left: item.position + '%' }"
          :title="item.text"
      >
        {{ item.text }}
      </span>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";

export default {
  name: "EiScaleRowsTable",

  components: {
    QuestionCircleOutlined,
  },

  props: {
    sections: {
      type: Array,
      default: () => [],
    },

    min: {
      type: Number,
      default: 50,
    },

    max: {
      type: Number,
      default: 150,
    },

    mode: {
      type: String,
      default: "single",
      validator: (value) => ["single", "compare"].includes(value),
    },

    defaultColor: {
      type: String,
      default: "#85A5FF",
    },

    defaultCompareColor: {
      type: String,
      default: "#D5DBE2",
    },

    scaleHeight: {
      type: Number,
      default: 4,
    },

    compareScaleHeight: {
      type: Number,
      default: 8,
    },

    rowGap: {
      type: Number,
      default: 12,
    },

    showDifference: {
      type: Boolean,
      default: true,
    },

    showTrackValues: {
      type: Boolean,
      default: false,
    },

    showScaleAfterEachSection: {
      type: Boolean,
      default: false,
    },

    showFirstSectionTitle: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const cssVars = computed(() => ({
      "--ei-scale-height": `${props.scaleHeight}px`,
      "--ei-compare-scale-height": `${props.compareScaleHeight}px`,
      "--ei-row-gap": `${props.rowGap}px`,
    }));

    const toNumber = (value) => {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    };

    const getWidth = (value) => {
      const number = toNumber(value);

      if (number <= props.min) {
        return 0;
      }

      if (number >= props.max) {
        return 100;
      }

      return Number((((number - props.min) / (props.max - props.min)) * 100).toFixed(2));
    };

    const guidePositions = computed(() => [
      getWidth(80),
      getWidth(100),
      getWidth(120),
    ]);

    const scaleLabels = computed(() => [
      {
        key: "min",
        type: "number",
        text: "50",
        position: getWidth(50),
      },
      {
        key: "low",
        type: "label",
        text: "Низкий",
        position: 15,
      },
      {
        key: "eighty",
        type: "number",
        text: "80",
        position: getWidth(80),
      },
      {
        key: "average",
        type: "label",
        text: "Средний",
        position: 40,
      },
      {
        key: "hundred",
        type: "number",
        text: "100",
        position: getWidth(100),
      },
      {
        key: "high",
        type: "label",
        text: "Высокий",
        position: 60,
      },
      {
        key: "one-twenty",
        type: "number",
        text: "120",
        position: getWidth(120),
      },
      {
        key: "highest",
        type: "label",
        text: "Высший",
        position: 85,
      },
      {
        key: "max",
        type: "number",
        text: "150",
        position: getWidth(150),
      },
    ]);

    const getDifference = (value, compareValue) => {
      return toNumber(value) - toNumber(compareValue);
    };

    const normalizedSections = computed(() => {
      return props.sections.map((section) => ({
        ...section,
        rows: Array.isArray(section.rows)
            ? section.rows.map((row) => {
              const value = toNumber(row.value);
              const compareValue = toNumber(row.compareValue);

              return {
                ...row,
                value,
                compareValue,
                difference: row.difference !== undefined && row.difference !== null
                    ? toNumber(row.difference)
                    : getDifference(value, compareValue),
                color: row.color || props.defaultColor,
                compareColor: row.compareColor || props.defaultCompareColor,
                muted: Boolean(row.muted),
              };
            })
            : [],
      }));
    });

    const formatValue = (value) => {
      const number = toNumber(value);

      return Number.isInteger(number)
          ? number
          : number.toFixed(1);
    };

    const formatDifference = (value) => {
      return formatValue(Math.abs(toNumber(value)));
    };

    const getDifferenceClass = (value) => {
      const number = toNumber(value);

      return {
        "ei-scale-table__difference--positive": number >= 10,
        "ei-scale-table__difference--neutral": number < 10 && number >= 0,
        "ei-scale-table__difference--negative": number < 0,
      };
    };

    const shouldShowSectionTitle = (section, sectionIndex) => {
      if (!section.title) {
        return false;
      }

      if (sectionIndex === 0 && !props.showFirstSectionTitle) {
        return false;
      }

      return true;
    };

    return {
      cssVars,
      guidePositions,
      scaleLabels,
      normalizedSections,
      getWidth,
      formatValue,
      formatDifference,
      getDifferenceClass,
      shouldShowSectionTitle,
    };
  },
};
</script>

<style scoped lang="scss">
.ei-scale-table {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.ei-scale-table__section-title {
  margin: 0 0 12px;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-scale-table__rows {
  position: relative;
  display: flex;
  flex-direction: column;
}

.ei-scale-table__rows-guides {
  position: absolute;
  left: 0;
  right: 0;
  top: -6px;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.ei-scale-table__rows-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(0, 0, 0, 0.08);
}

.ei-scale-table__row {
  position: relative;
  margin-bottom: var(--ei-row-gap);
}

.ei-scale-table__row_muted {
  .ei-scale-table__fill {
    opacity: 0.4;
  }

  .ei-scale-table__track-value {
    opacity: 0.4;
    color: rgba(0, 0, 0, 0.88);
  }
}

.ei-scale-table__row-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__label {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.ei-scale-table__label span:first-child {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ei-scale-table__help-icon {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.35);
  font-size: 14px;
  cursor: pointer;
}

.ei-scale-table__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.ei-scale-table__right {
  flex: 0 0 auto;
  min-width: max-content;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.ei-scale-table__value {
  flex: 0 0 auto;
  min-width: 32px;
  text-align: right;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__difference {
  flex: 0 0 auto;
  min-width: 34px;
  text-align: right;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}

.ei-scale-table__difference--positive {
  color: var(--main-green);
}

.ei-scale-table__difference--neutral {
  color: rgba(0, 0, 0, 0.25);
}

.ei-scale-table__difference--negative {
  color: #F55565;
}

.ei-scale-table__scale-area {
  position: relative;
  z-index: 1;
  width: 100%;
  height: var(--ei-scale-height);
}

.ei-scale-table__compare-area {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.ei-scale-table__track {
  position: relative;
  width: 100%;
  height: var(--ei-scale-height);
  overflow: visible;
  background-color: rgba(238, 240, 243, 0.85);
  border-radius: 999px;
}

.ei-scale-table--compare .ei-scale-table__track {
  height: var(--ei-compare-scale-height);
}

.ei-scale-table__fill {
  height: 100%;
  min-width: 0;
  border-radius: 999px;
}

.ei-scale-table__track-value {
  position: absolute;
  top: 50%;
  transform: translate(8px, -50%);
  z-index: 3;
  white-space: nowrap;
  font-weight: 600;
  font-size: 10px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__track-value--compare {
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__track-value--main {
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__scale {
  position: relative;
  width: 100%;
  height: 18px;
  margin: 4px 0 20px;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.28);
}

.ei-scale-table__scale-item {
  position: absolute;
  top: 0;
  display: block;
  min-width: 0;
  max-width: 58px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.ei-scale-table__scale-item--number,
.ei-scale-table__scale-item--label {
  transform: translateX(-50%);
}

.ei-scale-table__scale-item--first {
  transform: translateX(0);
  text-align: left;
}

.ei-scale-table__scale-item--last {
  transform: translateX(-100%);
  text-align: right;
}

.ei-scale-table__bottom-scale {
  margin-top: 4px;
  margin-bottom: 0;
}

.ei-scale-table > :last-child {
  margin-bottom: 0;
}

@media (max-width: $mobile-breakpoint) {
  .ei-scale-table__scale-item--label {
    max-width: 30px;
  }
}
</style>